import * as z from 'zod'

import {getEnabledFeatures} from '@github-ui/feature-flags'

import {
  ComparisonColumnSchema,
  type ComparisonColumn,
  type ComparisonGroup,
  type ComparisonStatus,
  type ComparisonTable,
  type ComparisonView,
} from '../../../schemas/contentful/contentTypes/comparisonTable'
import type {RichText} from '../../../schemas/contentful/richText'

export function selectComparisonColumns(component: ComparisonTable, view: string): ComparisonColumn[] {
  const selected = z
    .custom<ComparisonView>(candidate => candidate !== undefined, {
      message: `Comparison table "${component.sys.id}": requested view "${view}" does not exist`,
    })
    .parse(component.fields.views.find(candidate => candidate.fields.key === view))

  const needsFlags = selected.fields.columns.some(column => column.fields.enabled && column.fields.featureFlag?.trim())
  const enabled = new Set<string>(needsFlags ? getEnabledFeatures() : [])
  const columns = selected.fields.columns.filter(
    ({fields}) => fields.enabled && (!fields.featureFlag?.trim() || enabled.has(fields.featureFlag)),
  )

  return z
    .array(ComparisonColumnSchema)
    .min(
      2,
      `Comparison table "${component.sys.id}", view "${view}": ${columns.length} visible columns; at least 2 required`,
    )
    .parse(columns)
}

type RowFields = ComparisonGroup['fields']['rows'][number]['fields']
type RowValue = {
  tier: ComparisonColumn['fields']['tierKey']
  status: ComparisonStatus
  content?: RichText
}

export function comparisonRowValues(fields: RowFields): RowValue[] {
  return [
    {tier: 'free', status: fields.freeStatus, content: fields.freeContent},
    ...('teamStatus' in fields
      ? [{tier: 'team' as const, status: fields.teamStatus, content: fields.teamContent}]
      : [
          {tier: 'pro' as const, status: fields.proStatus, content: fields.proContent},
          {tier: 'proPlus' as const, status: fields.proPlusStatus, content: fields.proPlusContent},
          {tier: 'max' as const, status: fields.maxStatus, content: fields.maxContent},
          {tier: 'business' as const, status: fields.businessStatus, content: fields.businessContent},
        ]),
    {tier: 'enterprise', status: fields.enterpriseStatus, content: fields.enterpriseContent},
  ]
}

export function selectComparisonRowValues<T extends RowValue>(values: T[], columns: ComparisonColumn[]): T[] {
  return columns.map(({fields}) =>
    z
      .custom<T>(value => value !== undefined, {
        message: `Comparison row has no value for tier "${fields.tierKey}"`,
      })
      .parse(values.find(value => value.tier === fields.tierKey)),
  )
}
